import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MenuItem, Typography, styled } from '@mui/material'
import { motion } from 'framer-motion'
import { saveAs } from 'file-saver'


// CHART
import BarChart from '../../../component/charts/BarChart'
// import HorizontalChart from '../../../component/charts/HorizontalChart'
import VerticalBarChart from '../../../component/charts/VerticalBarChart'

// COMPONENT
import * as styles from '../style/main.style'
import Header from '../Header'
import Navbar from '../Navbar'
import InputTextField from '../../../component/input/inputTextField'
import FilterSelect from '../../../component/select/filterSelect'
import SearchAutocompleteSelect from '../../../component/select/searchAutocompleteSelect'
import { containerTransition, itemTransition } from '../../../constants/animation.framer';

// API
import { DepartMentApi } from '../../../api/department/department.api'
import { FiscalYearApi } from '../../../api/fiscal-year/fiscalyear.api'
import { StrategyApi } from '../../../api/strategy/strategy.api'
import { TacticsApi } from '../../../api/tactics/tactics.api'
import { PlanApi } from '../../../api/plan/plan.api'
import { TypesApi } from '../../../api/type/type.api'
import { ClustersApi } from '../../../api/clusters/clusters.api'
import { BudgetApi } from '../../../api/budget/budget.api'
import { ProjectApi } from '../../../api/project/project.api'
import { debouncedApiCall } from '../../../utils/app.utils'
import { DashboardApi, dashboardProps } from '../../../api/dashboard/dashboard.api';
import { useDispatch, useSelector } from 'react-redux'
import { setLoading, unsetLoading } from '../../../store/slice/loading/loading.slice'
import Loading from '../../../component/loading/Loading'
import MenuDropdown from '../../../component/menu-dropdown/MenuDropdown'
import { ContainerStatistics } from './statistics.style'

const FilterBox = styled('section')({
    width: "100%",
    height: "auto",
})

type DataSet = {
    label: string;
    data: number[];
    backgroundColor: string;
}


const Statistics: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch()
    const isLoading = useSelector((state: any) => state?.loading?.isLoading);

    const [idDepartment, setIdDepartment] = useState<number>(Number);
    const [keyword, setKeyword] = useState<string>("");
    const [tracks, setTracks] = useState<any[]>([]);
    const [datasets, setDatasets] = useState<DataSet[]>([]);


    const [countAll, setCountAll] = useState<{ graft_one: number, graft_two: number, graft_three: number, graft_four: number }>({
        graft_one: 0,
        graft_two: 0,
        graft_three: 0,
        graft_four: 0
    })
    const [trackPlan, setTrackPlan] = useState<any>({
        labels: [],
        datasets: [
            {
                label: '',
                data: [],
                data_api: [],
                backgroundColor: ['#90BE6D', '#F94144', '#5084bc', '#afbc50', '#bc8850'],
                hoverOffset: 4,
            }
        ],
    })
    const [trackStatus, setTrackStatus] = useState<any>({
        labels: [],
        datasets: [
            {
                label: '',
                data: [],
                data_api: [],
                backgroundColor: ['#5084bc', '#afbc50', '#F94144', '#90BE6D', '#bc8850'],
                hoverOffset: 4,
            }
        ],
    })
    const [trackStrategy, setTrackStrategy] = useState<any>({
        labels: [],
        datasets: [
            {
                label: '',
                data: [],
                data_api: [],
                backgroundColor: ['#afbc50', '#F94144', '#bc8850', '#90BE6D', '#5084bc'],
                hoverOffset: 4,
            }
        ],
    })
    const [trackDepartmentLabel, setTrackDepartmentLabel] = useState<any[]>([]);
    const [trackDepartmentDataset, setTrackDepartmentDataset] = useState<any[]>([]);

    const [asPlanCount, setAsPlanCount] = useState<any[]>([]);
    const [projectCount, setProjectCount] = useState<any[]>([]);
    const [completeCount, setCompleteCount] = useState<any[]>([]);
    const [maxValue, setMaxValue] = useState<number>(0);


    const [trackPlanOption, setTrackPlanOption] = useState<any[]>([]);


    const [idFiscalYear, setIdFiscalYear] = useState<number>(Number);
    const [idStrategy, setIdStrategy] = useState<number>(Number);
    const [idTactics, setIdTactics] = useState<number>(Number);
    const [idPlan, setIdPlan] = useState<number>(Number);
    const [idType, setIdType] = useState<number>(Number);
    const [idClusters, setIdClusters] = useState<number>(Number);
    const [idBudget, setIdBudget] = useState<number>(Number);
    const [idTrack, setIdTrack] = useState<number>(Number);

    const handleChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => debouncedApiCall({ searchItem: event.target.value, setItem: setKeyword })

    const handleGetProjectTrackStatus = async () => {
        await ProjectApi.getProjectTrack()
            .then(res => {
                if (res.status === 200) {
                    setTracks(res.data?.result)
                }
            })
            .catch(error => {
                console.log(error)
            })
    }

    const handleGetDashboard = useCallback(async () => {
        let condition: dashboardProps = {}
        if (keyword) condition = { ...condition, keyword }
        if (idFiscalYear) condition = { ...condition, idFiscalyear: idFiscalYear }
        if (idPlan) condition = { ...condition, idPlan }
        if (idStrategy) condition = { ...condition, idStrategy }
        if (idBudget) condition = { ...condition, idBudget }
        if (idType) condition = { ...condition, idType }
        if (idClusters) condition = { ...condition, idClusters }
        if (idDepartment) condition = { ...condition, idDepartment }
        if (idTrack) condition = { ...condition, idTrack }
        if (idTactics) condition = { ...condition, idTactics }

        await DashboardApi.projectTrackPlan(condition)
            .then(res => {
                if (res.status === 200) {
                    // console.log(res.data?.result)
                    const color = ['#90BE6D', '#F94144', '#5084bc', '#afbc50', '#bc8850'];
                    const data = res.data?.result?.map((list: any) => Number(...list.projectCount))
                    const countValue: number = res.data?.result?.reduce((a: any, b: any) => a + +b?.projectCount, 0) || 0;
                    setCountAll((prev) => ({
                        ...prev,
                        graft_three: countValue
                    }))
                    const data_api = res.data?.result?.map((x: any, index: number) => {
                        return {
                            name: x?.statusName,
                            y: +x?.projectCount || 0,
                            color: color[index],
                        }
                    })
                    const optionLabel = res.data?.result?.map((list: any) => list?.statusName)
                    if (data.length > 0 && optionLabel.length > 0 && data_api.length > 0) {
                        setTrackPlan((prev: any) => ({
                            ...prev,
                            labels: [...optionLabel],
                            data_api: data_api,
                            datasets: prev?.datasets?.map((list: any) => ({
                                ...list,
                                optionLabel: optionLabel,
                                data: [...data]
                            }))
                        }))
                    } else {
                        setTrackPlan({
                            labels: [],
                            datasets: [
                                {
                                    label: '',
                                    data: [],
                                    data_api: [],
                                    backgroundColor: ['#90BE6D', '#F94144', '#5084bc', '#afbc50', '#bc8850'],
                                    hoverOffset: 4,
                                }
                            ],
                        })
                    }
                }
            })
            .catch(error => {
                console.log(error)
            })
    }, [idFiscalYear, keyword, idPlan, idStrategy, idBudget, idType, idClusters, idDepartment, idTrack, idTactics])
    const handleGetDashboardTrackStatus = useCallback(async () => {
        let condition: dashboardProps = {}
        if (keyword) condition = { ...condition, keyword }
        if (idFiscalYear) condition = { ...condition, idFiscalyear: idFiscalYear }
        if (idPlan) condition = { ...condition, idPlan }
        if (idStrategy) condition = { ...condition, idStrategy }
        if (idBudget) condition = { ...condition, idBudget }
        if (idType) condition = { ...condition, idType }
        if (idClusters) condition = { ...condition, idClusters }
        if (idDepartment) condition = { ...condition, idDepartment }
        if (idTrack) condition = { ...condition, idTrack }
        if (idTactics) condition = { ...condition, idTactics }

        await DashboardApi.projectTrackStatus(condition)
            .then(res => {
                if (res.status === 200) {
                    // console.log(res.data?.result)
                    const color = ['#5084bc', '#afbc50', '#F94144', '#90BE6D', '#bc8850'];
                    const data = res.data?.result?.map((list: any) => Number(...list.projectCount))
                    const countValue: number = res.data?.result?.reduce((a: any, b: any) => a + +b?.projectCount, 0) || 0;
                    setCountAll((prev) => ({
                        ...prev,
                        graft_two: countValue
                    }))
                    const optionLabel = res.data?.result?.map((list: any) => list?.trackName)
                    const data_api = res.data?.result?.map((x: any, index: number) => {
                        return {
                            name: x?.trackName,
                            y: +x?.projectCount || 0,
                            color: color[index],
                        }
                    })
                    if (data && optionLabel) {
                        setTrackStatus((prev: any) => ({
                            ...prev,
                            labels: [...optionLabel],
                            data_api: data_api,
                            datasets: prev?.datasets?.map((list: any) => ({
                                ...list,
                                data: [...data]
                            }))
                        }))
                    }
                }
            })
            .catch(error => {
                console.log(error)
            })
    }, [idFiscalYear, keyword, idPlan, idStrategy, idBudget, idType, idClusters, idDepartment, idTrack, idTactics])
    const handleGetDashboardTrackStrategy = useCallback(async () => {
        let condition: dashboardProps = {}
        if (keyword) condition = { ...condition, keyword }
        if (idFiscalYear) condition = { ...condition, idFiscalyear: idFiscalYear }
        if (idPlan) condition = { ...condition, idPlan }
        if (idStrategy) condition = { ...condition, idStrategy }
        if (idBudget) condition = { ...condition, idBudget }
        if (idType) condition = { ...condition, idType }
        if (idClusters) condition = { ...condition, idClusters }
        if (idDepartment) condition = { ...condition, idDepartment }
        if (idTrack) condition = { ...condition, idTrack }
        if (idTactics) condition = { ...condition, idTactics }

        await DashboardApi.strategy(condition)
            .then(res => {
                if (res.status === 200) {
                    const color = ['#afbc50', '#F94144', '#bc8850', '#90BE6D', '#5084bc'];
                    const data = res.data?.result?.map((list: any) => Number(...list?.projectCount))
                    const optionLabel = res.data?.result?.map((list: any) => list?.strategyName);
                    const countValue: number = res.data?.result?.reduce((a: any, b: any) => a + +b?.projectCount, 0) || 0;
                    setCountAll((prev) => ({
                        ...prev,
                        graft_one: countValue
                    }))
                    const data_api = res.data?.result?.map((x: any, index: number) => {
                        return {
                            name: x?.strategyName,
                            y: +x?.projectCount || 0,
                            color: color[index],
                        }
                    })
                    if (data && optionLabel) {
                        setTrackStrategy((prev: any) => ({
                            ...prev,
                            labels: [...optionLabel],
                            data_api: data_api,
                            datasets: prev?.datasets?.map((list: any) => ({
                                ...list,
                                data: [...data]
                            })),
                            scaleShowValues: true,
                            scales: {
                                x: {
                                    ticks: {
                                        autoSkip: false
                                    }
                                },
                            }
                        }))
                    }
                }
            })
            .catch(error => {
                console.log(error)
            })
    }, [idFiscalYear, keyword, idPlan, idStrategy, idBudget, idType, idClusters, idDepartment, idTrack, idTactics])
    const handleGetDashboardTrackDepartment = useCallback(async () => {
        let condition: dashboardProps = {}
        if (keyword) condition = { ...condition, keyword }
        if (idFiscalYear) condition = { ...condition, idFiscalyear: idFiscalYear }
        if (idPlan) condition = { ...condition, idPlan }
        if (idStrategy) condition = { ...condition, idStrategy }
        if (idBudget) condition = { ...condition, idBudget }
        if (idType) condition = { ...condition, idType }
        if (idClusters) condition = { ...condition, idClusters }
        if (idDepartment) condition = { ...condition, idDepartment }
        if (idTrack) condition = { ...condition, idTrack }
        if (idTactics) condition = { ...condition, idTactics }

        await DashboardApi.projectTrackDepartment(condition)
            .then(res => {
                if (res.status === 200) {
                    const optionLabel = res.data?.result?.map((list: any) => list?.departmentName)
                    const asPlanCount = res.data?.result?.map((list: any) => Number(list?.asPlanCount || '0'));
                    const projectCount = res.data?.result?.map((list: any) => Number(list?.projectCount || '0'));
                    const completeCount = res.data?.result?.map((list: any) => Number(list?.completeCount || '0'));
                    const countValue: number = res.data?.result?.reduce((a: any, b: any) => a + +b?.projectCount, 0) || 0;
                    setCountAll((prev) => ({
                        ...prev,
                        graft_four: countValue
                    }))
                    setTrackDepartmentLabel(optionLabel)
                    setAsPlanCount(asPlanCount)
                    setProjectCount(projectCount)
                    setCompleteCount(completeCount)
                    setTrackDepartmentDataset(res.data?.result)
                    const allValues: number[] = [...asPlanCount, ...projectCount, ...completeCount];
                    const maxVal: number = Math.max(...allValues);
                    setMaxValue(maxVal);
                    setDatasets([
                        { label: 'จำนวนโครงการ', data: projectCount, backgroundColor: 'rgba(153, 102, 255, 0.2)' },
                        { label: 'เป็นไปตามแผน', data: asPlanCount, backgroundColor: 'rgba(75, 192, 192, 0.2)' },
                        { label: 'ดำเนินการเสร็จสิ้น', data: completeCount, backgroundColor: 'rgba(255, 159, 64, 0.2)' },
                    ]);
                }
            })
            .catch(error => {
                console.log(error)
            })
    }, [idFiscalYear, keyword, idPlan, idStrategy, idBudget, idType, idClusters, idDepartment, idTrack, idTactics])
    const handleDownload = useCallback(async (format: 'pdf' | 'excel') => {
        dispatch(setLoading())
        let condition: dashboardProps = {
            format: format
        }
        if (keyword) condition = { ...condition, keyword }
        if (idFiscalYear) condition = { ...condition, idFiscalyear: idFiscalYear }
        if (idPlan) condition = { ...condition, idPlan }
        if (idStrategy) condition = { ...condition, idStrategy }
        if (idBudget) condition = { ...condition, idBudget }
        if (idType) condition = { ...condition, idType }
        if (idClusters) condition = { ...condition, idClusters }
        if (idDepartment) condition = { ...condition, idDepartment }
        if (idTrack) condition = { ...condition, idTrack }
        if (idTactics) condition = { ...condition, idTactics }

        await DashboardApi.download(condition)
            .then(res => {
                if (res.status === 200) {
                    dispatch(unsetLoading())
                    // console.log(res.data)
                    const data = res?.data
                    if (format === 'pdf') {
                        const blob = new Blob([data], { type: 'application/pdf' });
                        saveAs(blob, 'statistics-pdf-e-monitering');
                    } else if (format === 'excel') {
                        const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                        saveAs(blob, 'statistics-xlsx-e-monitering');
                    }
                }
            })
            .catch(error => {
                console.log(error)
                dispatch(unsetLoading())
            })
    }, [idFiscalYear, keyword, idPlan, idStrategy, idBudget, idType, idClusters, idDepartment, idTrack, idTactics])

    useEffect(() => {
        handleGetDashboard();
    }, [handleGetDashboard])
    useEffect(() => {
        handleGetDashboardTrackStatus();
    }, [handleGetDashboardTrackStatus])
    useEffect(() => {
        handleGetDashboardTrackStrategy();
    }, [handleGetDashboardTrackStrategy])
    useEffect(() => {
        handleGetDashboardTrackDepartment();
    }, [handleGetDashboardTrackDepartment])


    useEffect(() => {
        handleGetProjectTrackStatus();
    }, [])

    return (
        <styles.ContainerHome>
            {isLoading === true && <Loading />}
            <Navbar />
            <styles.MainContainer>
                <Header />
                <styles.Content
                    variants={containerTransition}
                    initial="hidden"
                    animate="visible"
                    transition={{ stiffness: 250, delay: .4 }}
                >
                    <section>
                        <Typography className='text-primary' variant='h4'>
                            {t("สถิติข้อมูล")}
                        </Typography>
                    </section>
                    {/* ROW 1 */}
                    <FilterBox className='grid grid-cols-10 gap-1 mt-8'>
                        <article className='col-span-10 md:col-span-5 lg:col-span-2'>
                            <SearchAutocompleteSelect
                                headingNormal
                                heading={t("ปีงบประมาณ")}
                                labelId="label-fiscal-year"
                                idKey="idFiscalYear"
                                value={idFiscalYear}
                                serverSearch={false}
                                fetchOptions={(condition) => FiscalYearApi.getFiscalYear(condition)}
                                getOptionLabel={(option) => String(option?.fiscalYear ?? '')}
                                statusKey="fiscalYearStatus"
                                onChange={(id) => {
                                    setIdFiscalYear(id)
                                    setIdStrategy(0)
                                    setIdTactics(0)
                                    setIdType(0)
                                    setIdClusters(0)
                                }}
                            />
                        </article>
                        <article className='col-span-10 md:col-span-5 lg:col-span-2'>
                            <SearchAutocompleteSelect
                                headingNormal
                                heading={t("หน่วยงาน")}
                                labelId="label-agency"
                                idKey="idDepartment"
                                value={idDepartment}
                                fetchOptions={(condition) => DepartMentApi.getDepartments(condition)}
                                getOptionLabel={(option) => option?.departmentName}
                                statusKey="departmentStatus"
                                onChange={(id) => setIdDepartment(id)}
                            />
                        </article>
                        <article className='col-span-10 md:col-span-5 lg:col-span-2'>
                            <InputTextField
                                onchange={handleChangeSearch}
                                headingNormal
                                heading='ค้นหาตามชื่อโครงการ'
                            />
                        </article>
                        <article className='col-span-10 md:col-span-5 lg:col-span-2'>
                            <SearchAutocompleteSelect
                                headingNormal
                                heading={t("ยุทธศาสตร์")}
                                labelId="label-strategic"
                                idKey="idStrategy"
                                value={idStrategy}
                                params={{ idFiscalYear: idFiscalYear }}
                                fetchOptions={(condition) => StrategyApi.getStrategy(condition)}
                                getOptionLabel={(option) => option?.strategyName}
                                statusKey="strategyStatus"
                                onChange={(id) => {
                                    setIdStrategy(id)
                                    setIdTactics(0)
                                }}
                            />
                        </article>
                        <article className='col-span-10 md:col-span-5 lg:col-span-2'>
                            <SearchAutocompleteSelect
                                headingNormal
                                heading={t("กลยุทธ์")}
                                labelId="label-tactics"
                                idKey="idTactics"
                                value={idTactics}
                                params={{ idStrategy: idStrategy }}
                                fetchOptions={(condition) => TacticsApi.getTactics(condition)}
                                getOptionLabel={(option) => option?.tacticsName}
                                statusKey="tacticsStatus"
                                onChange={(id) => setIdTactics(id)}
                            />
                        </article>
                    </FilterBox>

                    {/* ROW 2 */}
                    <FilterBox className='grid grid-cols-10 mt-1 gap-1'>
                        <article className='col-span-10 md:col-span-5 lg:col-span-2'>
                            <SearchAutocompleteSelect
                                headingNormal
                                heading={t("แผนงาน")}
                                labelId="label-plan"
                                idKey="idPlan"
                                value={idPlan}
                                fetchOptions={(condition) => PlanApi.getPlans(condition)}
                                getOptionLabel={(option) => option?.planName}
                                statusKey="planStatus"
                                onChange={(id) => setIdPlan(id)}
                            />
                        </article>
                        <article className='col-span-10 md:col-span-5 lg:col-span-2'>
                            <SearchAutocompleteSelect
                                headingNormal
                                heading={t("ประเภทกลุ่มภารกิจ (Clusters)")}
                                labelId="label-typeOfMissionGroup"
                                idKey="idClusters"
                                value={idClusters}
                                params={{ idFiscalYear: idFiscalYear }}
                                fetchOptions={(condition) => ClustersApi.getClusters(condition)}
                                getOptionLabel={(option) => option?.clustersName}
                                statusKey="clustersStatus"
                                onChange={(id) => setIdClusters(id)}
                            />
                        </article>
                        <article className='col-span-10 md:col-span-5 lg:col-span-2'>
                            <SearchAutocompleteSelect
                                headingNormal
                                heading={t("แหล่งที่มางบประมาณ")}
                                labelId="label-budget"
                                idKey="idBudget"
                                value={idBudget}
                                fetchOptions={(condition) => BudgetApi.getBudgets(condition)}
                                getOptionLabel={(option) => option?.budgetName}
                                statusKey="budgetStatus"
                                onChange={(id) => setIdBudget(id)}
                            />
                        </article>
                        <article className='col-span-10 md:col-span-5 lg:col-span-2'>
                            <SearchAutocompleteSelect
                                headingNormal
                                heading={t("ประเภทโครงการ")}
                                labelId="label-typeProject"
                                idKey="idType"
                                value={idType}
                                params={{ idFiscalYear: idFiscalYear }}
                                fetchOptions={(condition) => TypesApi.getType(condition)}
                                getOptionLabel={(option) => option?.typeName}
                                statusKey="typeStatus"
                                onChange={(id) => setIdType(id)}
                            />
                        </article>
                        <article className='col-span-10 md:col-span-5 lg:col-span-2'>
                            <FilterSelect
                                headingNormal
                                heading={t("สถานะการติดตาม")}
                                renderValue={() => idTrack === 0 ? "ทั้งหมด" : tracks?.find((list: any) => list?.idTrack === idTrack)?.trackName}
                                label={t('')}
                                selectId="select-typeOfMissionGroup"
                                value={idTrack}
                                labelId="label-typeOfMissionGroup"
                                onchange={(event) => {
                                    const value = event.target.value
                                    value && setIdTrack(Number(value))
                                }}
                                options={[
                                    <MenuItem onClick={() => setIdTrack(0)} value={0}>{"ทั้งหมด"}</MenuItem>,
                                    tracks && tracks?.map((list: any, index: number) => (
                                        <MenuItem key={list?.idTrack} value={list?.idTrack}>
                                            {`${index + 1}.${list?.trackName}`}
                                        </MenuItem>
                                    ))]}
                            />
                        </article>
                    </FilterBox>
                    {/* ROW 3 */}
                    <FilterBox className='my-3'>
                        <article className='col-span-12 flex justify-start md:justify-end items-center gap-1'>
                            <span className='w-full max-w-[93px] flex flex-col self-end gap-2'>
                                <MenuDropdown
                                    headingNormal
                                    heading='ส่งออกเอกสาร'
                                    buttonName='ดาวน์โหลด'
                                    menuItems={[
                                        { id: 1, name: "pdf", onClick: () => handleDownload('pdf') },
                                        { id: 2, name: 'excel', onClick: () => handleDownload('excel') }
                                    ]}
                                />
                            </span>
                        </article>
                    </FilterBox>
                    <ContainerStatistics>
                        <motion.section style={{ overflow: "auto" }} variants={itemTransition} className='w-auto flex-1 min-h-[427px] h-auto'>
                            <BarChart
                                enabled_line={false}
                                // Doughnut
                                option={trackPlanOption}
                                data={trackStrategy}
                                className='h-full'
                                maxWidth='341px'
                                heading={`การดำเนินงานในภาพรวม (${countAll.graft_one || 0} โครงการ)`} />
                        </motion.section>
                        <motion.section variants={itemTransition} className='w-auto flex-1 min-h-[427px] h-auto'>
                            <BarChart
                                enabled_line={true}
                                option={trackPlanOption}
                                data={trackStatus}
                                className='h-full'
                                maxWidth='341px'
                                heading={`ผลสำเร็จของการดำเนินงานในภาพรวม (${countAll.graft_two || 0} โครงการ)`} />
                        </motion.section>
                        <motion.section variants={itemTransition} className='w-auto flex-1 min-h-[427px] h-auto'>
                            <BarChart
                                enabled_line={true}
                                option={trackPlanOption}
                                data={trackPlan}
                                className='h-full'
                                maxWidth='341px'
                                heading={`แผนการดำเนินงานในภาพรวม (${countAll.graft_three || 0} โครงการ)`} />
                        </motion.section>
                    </ContainerStatistics>
                    <main className='w-full flex flex-col gap-3 lg:flex-row mt-2 overflow-hidden'>
                        <motion.section variants={itemTransition} className='w-full lg:w-full'>
                            <VerticalBarChart
                                max={maxValue * 1.2}
                                datasets={datasets}
                                labelProps={[...trackDepartmentLabel]}
                                heading={`แยกตามโครงการ (${countAll.graft_four || 0} โครงการ)`}
                                className='h-full min-h-[329px]'
                            />
                        </motion.section>
                        {/* <motion.section variants={itemTransition} className='w-full lg:w-1/2'>
                            <VerticalBarChart
                                datasets={[{
                                    data: [12, 22, 33, 43, 55, 100, 12, 33],
                                    label: '-',
                                    backgroundColor: colors.chart_red
                                }
                                ]}
                                labelProps={['H1', 'H2', 'Q3', 'Q4', 'Q5', 'Q6', 'Q7']}
                                heading='แยกตามแผนงาน'
                                className='h-full min-h-[329px]' />
                        </motion.section> */}
                    </main>
                </styles.Content>
            </styles.MainContainer>

        </styles.ContainerHome>
    )
}

export default Statistics
